/* eslint-disable camelcase */
import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import MediaQuery from 'react-responsive';
import { toast } from 'react-toastify';

import Box from '@components/Atoms/Box';
import Button from '@components/Atoms/Button';
import ScrollWrapper from '@components/Atoms/ScrollWrapper';
import ShowIf from '@components/Atoms/ShowIf';
import Typography from '@components/Atoms/Typography';
import ConfirmModal from '@components/ConfirmModal';
import {
  useContentFilterContext,
  useContentFilterSkillContext,
  useContentFilterStandardContext,
} from '@components/ContentFilters/ContentFilterProvider';
import {
  ActionShowHide,
  LeftSiderBarHeader,
  LeftSiderBarHeaderGroup,
  LeftSiderBarWrap,
} from '@components/LeftSideBar/LeftSiderBar.style';
import MainContent from '@components/MainContent';
import { Passage as PassageType } from '@components/QuestionGenerationPassages/types';
import Passages from '@components/QuestionGenerationPassages';
import QuestionSelectorProvider from '@components/QuestionSelector/QuestionSelector.provider';
import ReportErrorModal from '@components/ReportErrorModal/ReportErrorModal';
import { useUser } from '@reducers/user/hooks';
import { startMeasurement, measureTimeBetween } from '@utils/dataDogActions/Helpers';
import {
  trackQuestionGeneration,
  trackQuestionsSubmitted,
} from '@utils/dataDogActions/questionGenerationService';

import CourseSelect from '@containers/CourseSelect';
import SectionSelect from '@containers/SectionSelect';

import { BLOOMS_MAP } from '@components/Selectors/BloomsSelector/BloomsSelector';
import { DOK_MAP } from '@components/Selectors/DokSelector/DokSelector';
import {
  useCreateUserAcknowledgment,
  useGetUserAcknowledgments,
  useUpdateUserAcknowledgment,
} from 'chalktalk-react/hooks/userAcknowledgementHooks';
import { SERVICE_NAMES } from './constants/services';
import { CopyrightDialog, SubmissionDialog } from './Dialogs';
import {
  useGeneratedItem,
  usePostQGSQuestionRequest,
  useSubmitQuestionsToItemBank,
  usePostUserReportIssue,
  useGenerationStatusItems,
  useCoAuthorEnabled,
} from './hooks';
import QuestionGenerationEditor from './QuestionGenerationEditor';
import QuestionGenerationMenu from './QuestionGenerationMenu';
import QuestionGenerationQuestions from './QuestionGenerationQuestions';
import {
  StyledBlockWrap,
  QuestionGenerationMenuWrapper,
  EditorWrapper,
  QuestionNavigatorWrapper,
  PassageWrapper,
} from './styles';
import SubmissionList from './SubmissionList';
import {
  QuestionGenerationMenuType,
  QuestionGenerationServiceMenu,
  MenuObject,
  LearnosityItem,
} from './types';
import BlockNavigationAway from './BlockNavigationAway';

const FEATURE_ACK_NAME = 'COAUTHOR-COPYRIGHT-DISCLAIMER';

const initialState: QuestionGenerationServiceMenu = {
  subject: {},
  blooms: '2',
  dok: '2',
  questionQuantity: 5,
  includeSlides: false,
  customNeeds: '',
  lessonGroupId: undefined,
  questionType: '',
};

const reducer = (
  state: QuestionGenerationServiceMenu,
  action: { type: QuestionGenerationMenuType; payload },
) => {
  switch (action.type) {
    case QuestionGenerationMenuType.SET_SUBJECT:
      return { ...state, subject: action.payload };
    case QuestionGenerationMenuType.SET_BLOOMS:
      return { ...state, blooms: action.payload };
    case QuestionGenerationMenuType.SET_DOK:
      return { ...state, dok: action.payload };
    case QuestionGenerationMenuType.SET_QUESTION_QUANTITY:
      return { ...state, questionQuantity: action.payload };
    case QuestionGenerationMenuType.SET_INCLUDE_SLIDES:
      return { ...state, includeSlides: action.payload };
    case QuestionGenerationMenuType.SET_CUSTOM_NEEDS:
      return { ...state, customNeeds: action.payload };
    case QuestionGenerationMenuType.SET_QUESTION_TYPE:
      return { ...state, questionType: action.payload?.type };
    default:
      return state;
  }
};

interface QuestionGenerationServiceProps {
  onSubmitCallback?: () => void;
  inDialog?: boolean;
  onGenerateQuestions?: () => void;
  onDisclaimerReject: () => void;
}

const QuestionGenerationService: React.FC<QuestionGenerationServiceProps> = ({
  onSubmitCallback,
  inDialog,
  onGenerateQuestions,
  onDisclaimerReject,
}) => {
  const history = useHistory();
  const { subjectId: curriculumSubjectId, lessonId, courseId } = useParams();
  const coauthorEnabled = useCoAuthorEnabled(courseId);

  // fetch the current user
  const currentUser = useUser();

  // disclaimer dialog
  const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(false);
  const { data: acknowledgments } = useGetUserAcknowledgments(currentUser.id);
  const { mutate: createAck } = useCreateUserAcknowledgment(FEATURE_ACK_NAME);
  const { mutate: updateAck } = useUpdateUserAcknowledgment(FEATURE_ACK_NAME);

  const [isSideBarClose, setIsSideBarClose] = React.useState(true);

  // handles the question generation request and response will be stored in taskId
  const { mutate: fetchMutate, isLoading } = usePostQGSQuestionRequest();

  // taskId is the "session" id to scope the generated questions to the selected options
  const [taskId, setTaskId] = useState<string | null>(null);

  // prompt when user wants to reset the current session
  const [showResetModal, setShowResetModal] = useState(false);

  // holds the selected question, used to display the question in the editor
  const [selectedItem, setSelectedItem] = useState<LearnosityItem | null>(null);

  // prompt when user wants to submit the generated questions
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  // holds the deleted questions
  const [deletedItems, setDeletedItems] = useState<LearnosityItem[]>([]);

  // handles the submission of the questions to the Itembank
  const { mutate: submitMutate, isLoading: isSubmitting } =
    useSubmitQuestionsToItemBank();

  // control error report modal
  const [isReportErrorModalOpen, setIsReportErrorModalOpen] = useState(false);

  // post user report issue
  const { mutate: postUserReportIssue } = usePostUserReportIssue();

  // hold the state of the "generating" questions while waiting for QGS response via websocket
  // null means no request made, true means request made, false means request completed
  const [generating, setGenerating] = useState<boolean | null>(null);

  // state to control the question delete modal
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const [actionType, setActionType] = useState<'submit' | 'reset' | null>(null);

  const [passageMode, setPassageMode] = useState(false);

  const { generationStatuses, clearStatuses } = useGenerationStatusItems(taskId);

  const { selectedStandards } = useContentFilterStandardContext();
  const { selectedSkills } = useContentFilterSkillContext();
  const {
    subjects,
    curriculumSubjects,
    setSubjectOverride,
    selectedPassage,
    setSelectedPassage,
    clearSelection,
  } = useContentFilterContext();

  // default subject is the first subject in the list
  // if curriculumSubjectId is provided from the param, it will be used to find the subject
  const defaultSubject = useMemo(() => {
    if (curriculumSubjectId) {
      const foundSubject = curriculumSubjects.find(
        (cs) => cs.id === parseInt(curriculumSubjectId, 10),
      );
      return foundSubject?.subject ?? subjects[0];
    }

    return subjects[0];
  }, [curriculumSubjectId, subjects, curriculumSubjects]);

  // holds the menu selection state
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    subject: defaultSubject as unknown as MenuObject,
    lessonGroupId: lessonId ? parseInt(lessonId, 10) : undefined,
  });

  const { items, addItem, removeItem, clearItems, updateItem, firstQuestionTimeRef } =
    useGeneratedItem(taskId, setGenerating, state.subject, state.blooms, selectedPassage);

  // This is to allow submission of custom questions with no taskId
  const areAllItemsCustom = items.every((i): boolean => i.is_custom);

  // When the user changes any of the menu options, close passage mode
  useEffect(() => {
    setPassageMode(false);
  }, [state]);

  // When the subject changes, update the subject override for the FilterContentProvider
  // to fetch the correct skills and standards
  useEffect(() => {
    if (!state.subject) {
      return;
    }
    setSubjectOverride(state.subject.id);
    if (!inDialog) {
      clearSelection();
    }
  }, [clearSelection, inDialog, setSubjectOverride, state.subject]);

  // Track whether metrics have been sent
  const metricsSentRef = useRef(false);

  // Add timing ref for measuring question generation time
  const generationStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    // When a new taskId is set, update the firstQuestionTimeRef
    if (taskId && firstQuestionTimeRef) {
      // Store the current generation start time
      firstQuestionTimeRef.current = {
        ...firstQuestionTimeRef.current,
        generationStartTime: generationStartTimeRef.current,
      };
    }
  }, [taskId, firstQuestionTimeRef]);

  // Reset metrics sent flag when starting a new generation
  useEffect(() => {
    if (generating === true) {
      metricsSentRef.current = false;

      // Capture current values for use in the timeout
      const currentTaskId = taskId;

      // Set up a timeout to check for long-running generations
      const timeoutId = setTimeout(() => {
        // If we're still generating after the timeout and haven't sent metrics
        if (
          generating === true &&
          !metricsSentRef.current &&
          generationStartTimeRef.current &&
          currentTaskId
        ) {
          const longRunningTime = measureTimeBetween(generationStartTimeRef.current);

          // Send timing metric for long-running generation
          trackQuestionGeneration({
            taskId,
            subject: state.subject?.name as string,
            questionType: String(state.questionType || 'N/A'),
            value: 1,
            requestedCount: 0,
            generatedCount: 0,
            failedCount: 0,
            generationTime: longRunningTime,
            tags: {
              status: 'in_progress',
              reason: 'long_running',
            },
          });

          // Just record this as an interim measurement for long-running generations.
          // Complete metrics will be sent on completion as a single batch action for all questions.
        }
      }, 60000); // 1 minute timeout

      // Clear the timeout if generation state changes before timeout
      return () => clearTimeout(timeoutId);
    }
  }, [generating, taskId, state.subject?.name, state.questionType]);

  // Monitor ongoing generation with periodic checks
  useEffect(() => {
    // Only set up the interval if we're generating
    if (generating === true) {
      // Capture current values for use in the interval
      const currentTaskId = taskId;

      // Create an interval that sends metrics every minute for long-running generations
      const intervalId = setInterval(() => {
        if (
          generating === true &&
          !metricsSentRef.current &&
          generationStartTimeRef.current &&
          currentTaskId
        ) {
          const ongoingTime = measureTimeBetween(generationStartTimeRef.current);

          // Only send periodic metrics if generation has been running for more than 1 minute
          if (ongoingTime > 60000) {
            // Send timing metric for ongoing long generation
            trackQuestionGeneration({
              taskId,
              subject: state.subject?.name as string,
              questionType: String(state.questionType || 'N/A'),
              value: 1,
              requestedCount: 0,
              generatedCount: 0,
              failedCount: 0,
              generationTime: ongoingTime,
              tags: {
                status: 'ongoing',
                reason: 'periodic_check',
                duration_minutes: Math.floor(ongoingTime / 60000).toString(),
              },
            });
          }
        }
      }, 60000); // Check every minute

      // Clean up the interval when generation changes
      return () => clearInterval(intervalId);
    }
  }, [generating, taskId, state.subject?.name, state.questionType]);

  useEffect(() => {
    // Only check when generation completes and metrics haven't been sent yet
    if (generating === false && taskId && !metricsSentRef.current) {
      // If there are pending generation statuses, don't send metrics yet
      // Wait until all items have been processed
      if (generationStatuses.length > 0) {
        return;
      }

      // Mark metrics as sent so this doesn't run again for this generation
      metricsSentRef.current = true;

      const requestedCount = Number(state.questionQuantity);
      const generatedCount = items.length;

      // Count failed generations (these are removed from generationStatuses when completed)
      const failedCount = generationStatuses.filter(
        (status) => status.status === 'failed' || status.status === 'error',
      ).length;

      // Total attempts = successful + failed
      const totalAttemptedCount = generatedCount + failedCount;

      // Track generation time if we have a start time reference
      if (generationStartTimeRef.current) {
        const generationDuration = measureTimeBetween(generationStartTimeRef.current);

        if (totalAttemptedCount === requestedCount) {
          // Send metric about complete generation
          trackQuestionGeneration({
            taskId,
            subject: state.subject?.name as string,
            questionType: String(state.questionType || 'N/A'),
            value: 1,
            requestedCount,
            generatedCount,
            failedCount,
            generationTime: generationDuration,
          });
        } else if (totalAttemptedCount < requestedCount) {
          // Send metric about partial generation
          trackQuestionGeneration({
            taskId,
            subject: state.subject?.name as string,
            questionType: String(state.questionType || 'N/A'),
            value: generatedCount / requestedCount, // success rate
            requestedCount,
            generatedCount,
            failedCount,
            generationTime: generationDuration,
            errorType: 'incomplete_generation',
          });
        }
      }
    }
  }, [
    generating,
    items.length,
    generationStatuses,
    state.questionQuantity,
    taskId,
    state.subject?.display_name,
    state.questionType,
    state.subject?.name,
  ]);

  // Add a ref to track submission metrics sent by taskId
  const submissionMetricsSentRef = useRef<{ [key: string]: boolean }>({});

  // Helper type for the window with Cypress
  interface WindowWithCypress extends Window {
    Cypress?: any;
  }

  // Helper to safely check for Cypress test environment
  const isTestMode =
    typeof window !== 'undefined' && !!(window as WindowWithCypress).Cypress;

  /**
   * Get the payload to send to the backend for generating questions
   *
   * This will be used to generate the questions and when submitting custom questions
   */
  const getGenerationRequestPayload = useCallback(
    () => ({
      subject: state.subject?.id,
      skills: selectedSkills,
      standards: selectedStandards,
      blooms: BLOOMS_MAP[state.blooms],
      dok: DOK_MAP[state.dok],
      quantity: state.questionQuantity,
      include_slides: state.includeSlides,
      lessongroup: state.lessonGroupId,
      passage: selectedPassage,
      custom_needs: state.customNeeds,
      question_type: state.questionType,
    }),
    [
      state.subject?.id,
      state.blooms,
      state.dok,
      state.questionQuantity,
      state.includeSlides,
      state.lessonGroupId,
      state.customNeeds,
      state.questionType,
      selectedSkills,
      selectedStandards,
      selectedPassage,
    ],
  );

  // When error occurs, automatically open report dialog with taskId
  const handleError = (errorTaskId: string | null) => {
    setTaskId(errorTaskId);
    setIsReportErrorModalOpen(true);
  };

  /**
   * Trigger the generation request
   *
   * This will send the request to the API and store the taskId
   */
  const triggerGenerationRequest = () => {
    const payload = getGenerationRequestPayload();

    if (payload.standards.length === 0) {
      toast.error('Please select at least one Standard');
      return;
    }

    if (!payload.dok || !payload.blooms) {
      toast.error('Please select a DOK and Bloom level');
      return;
    }

    // Start timing - this will be used to measure time until first question
    generationStartTimeRef.current = startMeasurement();
    if (!payload.question_type) {
      toast.error('Please select a Question Type');
      return;
    }

    fetchMutate(payload, {
      onSuccess: ({ response }: { response: { data: string } }) => {
        const newTaskId = response.data;
        setTaskId(newTaskId);

        // Store the start time in QuestionGenerationEditor component props
        if (firstQuestionTimeRef && generationStartTimeRef.current) {
          firstQuestionTimeRef.current = {
            ...firstQuestionTimeRef.current,
            generationStartTime: generationStartTimeRef.current,
          };
        }
      },
      onError: () => {
        handleError(taskId);
      },
    });
    onGenerateQuestions?.();
    setGenerating(true);
  };

  /**
   * Add a custom question to the list of generated questions
   *
   * If there is no taskId, it will create and set a new taskId
   * If there is a taskId, it will use the existing taskId
   */
  const addCustomItem = () => {
    if (!taskId) {
      setTaskId(uuidv4());
    }
    addItem({
      response_id: uuidv4(),
      task_id: taskId as string,
      user_id: currentUser.id.toString(),
      is_custom: true,
      is_edited: true,
    });
  };

  /**
   * Check if the item is valid by checking if all questions have a type
   */
  const isItemValid = (item: LearnosityItem): boolean => {
    // For now we check if all questions have a type
    if (item.questions.some((q) => !q.type)) {
      return false;
    }
    return true;
  };

  /**
   * Sets the "selected" item from the list of generated questions
   */
  const selectItem = (referenceId: string) => {
    let currentItem: LearnosityItem | null = null;
    let targetItem: LearnosityItem | null = null;

    items.forEach((i) => {
      if (i.reference === referenceId) {
        targetItem = i as LearnosityItem;
      }
      if (i.reference === selectedItem?.reference) {
        currentItem = i as LearnosityItem;
      }
    });

    if (!currentItem || !targetItem) {
      return;
    }

    if (!isItemValid(currentItem)) {
      toast.error(
        `Cannot move to another question while custom mode is active.
        Please select a question type and save before making changes.`,
      );
      return;
    }

    setSelectedItem(targetItem);
  };

  const clearAllState = useCallback((): void => {
    clearItems();
    setSelectedItem(null);
    setDeletedItems([]);
    setTaskId(null);
    setGenerating(null);
    setActionType(null);
    clearStatuses();
  }, [clearItems, clearStatuses]);

  const submitDeletedItemsAndClear = useCallback(async (): Promise<void> => {
    setActionType('reset');
    // Mark all non-deleted items as deleted
    const itemsToDelete = items.map((item) => ({
      ...item,
      is_deleted: true,
    }));

    // Combine with already deleted items
    const allDeletedItems = [...deletedItems, ...itemsToDelete];

    // Close modal first to prevent UI freeze
    setShowResetModal(false);

    clearAllState();

    if (allDeletedItems.length === 0) {
      return;
    }

    const payload = {
      task_id: taskId,
      items: allDeletedItems,
      question_parameters: areAllItemsCustom ? getGenerationRequestPayload() : null,
      is_test: isTestMode,
      course: courseId,
    };

    try {
      await submitMutate(payload, {
        onError: () => {
          if (!areAllItemsCustom) {
            handleError(taskId);
          }
          // Reset loading state even on error
        },
      });
    } catch (error) {
      // Ensure loading states are cleared even if mutation throws
      if (!areAllItemsCustom) {
        handleError(taskId);
      }
    } finally {
      setGenerating(null);
    }
  }, [
    areAllItemsCustom,
    clearAllState,
    deletedItems,
    getGenerationRequestPayload,
    items,
    submitMutate,
    taskId,
    isTestMode,
    courseId,
  ]);

  // The original resetCallback becomes:
  const resetCallback = useCallback(() => {
    submitDeletedItemsAndClear();
  }, [submitDeletedItemsAndClear]);

  /**
   * Delete an item from the list of generated questions
   *
   * If the item is not a custom question, it will be added to the list of deletedItems
   */
  const triggerDeleteItem = () => {
    const targetItem = items.find((i) => i.reference === itemToDelete);

    if (targetItem) {
      if (!targetItem.is_custom) {
        setDeletedItems((prev) => [
          ...prev,
          { ...(targetItem as LearnosityItem), is_deleted: true },
        ]);
      }

      removeItem(targetItem.reference);

      if (items.length === 1) {
        resetCallback();
      }
    }

    setItemToDelete(null);
  };

  /**
   * Prompt the user if they want to delete the item
   * Checks if the item is valid before deleting
   *
   */
  const deleteItem = (referenceId: string) => {
    const currentItem = items.find((i) => i.reference === selectedItem?.reference);

    if (!currentItem) {
      return;
    }

    if (!isItemValid(currentItem)) {
      toast.error(
        `Cannot delete questions in while custom mode is active.
        Please select a question type and save before making changes.`,
      );
      return;
    }

    setItemToDelete(referenceId);
  };

  /**
   * Validate then submit the items to the Itembank
   */
  const submitItems = () => {
    setActionType('submit');
    const invalidItems = items
      .map((item, index) => (!isItemValid(item) ? index + 1 : null))
      .filter((index) => index !== null) as number[];
    if (invalidItems.length) {
      toast.error(
        <div>
          <p>
            Validation Failed! Questions {invalidItems.join(', ')} need to be completed.
          </p>
        </div>,
      );
      return;
    }

    // Only submit active items
    const payload = {
      task_id: taskId,
      items,
      question_parameters: areAllItemsCustom ? getGenerationRequestPayload() : null,
      is_test: isTestMode,
      course: courseId,
    };

    // Calculate metrics for questions requested vs actually submitted
    const requestedCount = Number(state.questionQuantity);
    const submittedCount = items.length;
    const deletedCount = deletedItems.length;

    // Calculate submission rate as percentage (submitted / requested * 100)
    const submissionRate =
      requestedCount > 0 ? (submittedCount / requestedCount) * 100 : 0;

    // Send metrics to datadog only once per taskId
    if (taskId && !submissionMetricsSentRef.current[taskId]) {
      trackQuestionsSubmitted({
        taskId,
        subject: state.subject?.name as string,
        questionType: String(state.questionType || 'N/A'),
        value: submissionRate,
        requestedCount,
        submittedCount,
        deletedCount,
      });

      // Mark this taskId as having metrics sent
      submissionMetricsSentRef.current[taskId] = true;
    }

    submitMutate(payload, {
      onSuccess: () => {
        // After successful submission of active items, handle deleted items if any
        if (deletedItems.length > 0) {
          const deletedPayload = {
            task_id: taskId,
            items: deletedItems,
            question_parameters: null,
            is_test: isTestMode,
            course: courseId,
          };
          // Submit deleted items separately
          submitMutate(deletedPayload);
        }

        if (curriculumSubjectId) {
          clearAllState();
        } else {
          setShowSubmitDialog(true);
        }
        if (onSubmitCallback) {
          onSubmitCallback();
        }
      },
      onError: () => handleError(taskId),
    });
  };

  /**
   * When items change, this controls the selectedItem state
   */
  useEffect(() => {
    if (!items.length) {
      return;
    }

    // check if selectedItem.reference exists in items
    const selectedItemExists = items.some((i) => i.reference === selectedItem?.reference);

    if (!selectedItemExists) {
      setSelectedItem(items[0]);
    }
  }, [items, selectedItem]);

  // Update the handleSubmitError to include taskId automatically
  const handleSubmitError = (msg: string) => {
    postUserReportIssue(
      {
        trace_id: taskId || 'N/A', // Include the taskId automatically
        content_type: SERVICE_NAMES.CO_AUTHOR,
        content: msg,
      },
      {
        onSuccess: () => {
          toast.success('Report Submitted successfully');
          setIsReportErrorModalOpen(false);
          setTaskId(null); // Clear the taskId after submission
        },
      },
    );
  };

  const getLoadingMessage = () => {
    if (actionType === 'submit') {
      return 'Please wait while we save your questions';
    }
    if (actionType === 'reset') {
      return 'Please wait while we clean up your workspace';
    }
    return 'Please wait...';
  };

  const handlePassageSelection = (passage: PassageType) => {
    setSelectedPassage(passage);
    setPassageMode(false);
  };

  const newRouteRef = useRef(null);
  const isNavigatingAwayRef = useRef(false);
  const [showNavigationPrompt, setShowNavigationPrompt] = useState(false);

  // Add the history blocker
  const unblockRef = useRef(
    history.block((location) => {
      newRouteRef.current = location.pathname;

      // If we're generating questions and haven't sent metrics yet
      if (
        generating === true &&
        !metricsSentRef.current &&
        generationStartTimeRef.current &&
        taskId &&
        !isNavigatingAwayRef.current
      ) {
        isNavigatingAwayRef.current = true;
        setShowNavigationPrompt(true);
        return false;
      }

      unblockRef.current();
      return true;
    }),
  );

  // Handle navigation confirmation
  const handleNavigationConfirm = () => {
    // Send metrics before navigating away
    if (
      generating === true &&
      !metricsSentRef.current &&
      generationStartTimeRef.current &&
      taskId
    ) {
      const partialGenerationTime = measureTimeBetween(generationStartTimeRef.current);

      // Send timing metric with user_confirmed tag
      trackQuestionGeneration({
        taskId,
        subject: state.subject?.name as string,
        questionType: String(state.questionType || 'unknown'),
        value: partialGenerationTime,
        requestedCount: 0,
        generatedCount: 0,
        failedCount: 0,
        generationTime: partialGenerationTime,
        tags: {
          status: 'incomplete',
          reason: 'user_confirmed_navigation',
        },
      });

      metricsSentRef.current = true;
    }

    // Close prompt and unblock navigation
    setShowNavigationPrompt(false);
    if (unblockRef.current) {
      unblockRef.current();
      history.push(newRouteRef.current);
    }
  };

  // Handle navigation cancellation
  const handleNavigationCancel = () => {
    setShowNavigationPrompt(false);
    isNavigatingAwayRef.current = false;
  };

  // check if the user has acknowledged the disclaimer
  useEffect(() => {
    const featureAck = acknowledgments?.find((ack) => ack.feature === FEATURE_ACK_NAME);
    if (!featureAck) {
      setShowDisclaimerDialog(true);
      createAck();
    } else if (featureAck && !featureAck?.acknowledged) {
      setShowDisclaimerDialog(true);
    }
  }, [acknowledgments, createAck]);

  const handleAcceptDisclaimer = () => {
    updateAck({
      feature: FEATURE_ACK_NAME,
      acknowledged: true,
    });
    setShowDisclaimerDialog(false);
  };

  const handleDeclineDisclaimer = () => {
    updateAck({
      feature: FEATURE_ACK_NAME,
      acknowledged: false,
    });
    onDisclaimerReject();
  };

  return (
    <>
      <CopyrightDialog
        open={showDisclaimerDialog}
        onAccept={handleAcceptDisclaimer}
        onDecline={handleDeclineDisclaimer}
      />
      <ShowIf If={!inDialog}>
        <LeftSiderBarWrap
          {...({ isClose: isSideBarClose } as any)}
          aria-label="Course navigation"
        >
          <LeftSiderBarHeader>
            <LeftSiderBarHeaderGroup>
              <CourseSelect pathName="/coauthor/course" />
              <SectionSelect pathName="/coauthor/course" />
            </LeftSiderBarHeaderGroup>
          </LeftSiderBarHeader>
          <ActionShowHide
            className="toggle-icon"
            onClick={() => setIsSideBarClose(!isSideBarClose)}
          >
            <MediaQuery maxWidth={960}>
              {(matches) =>
                matches ? (
                  <span
                    className={
                      isSideBarClose ? 'chalktalk-icon-bold-up' : 'chalktalk-menu'
                    }
                  />
                ) : (
                  <span
                    className={
                      isSideBarClose ? 'chalktalk-menu' : 'chalktalk-icon-bold-up'
                    }
                  />
                )
              }
            </MediaQuery>
          </ActionShowHide>
        </LeftSiderBarWrap>
      </ShowIf>
      <MainContent isSideBarClose={isSideBarClose}>
        <SubmissionDialog
          open={showSubmitDialog}
          onClose={() => {
            setShowSubmitDialog(false);
            clearAllState();
          }}
          submissionList={
            <SubmissionList
              state={state as unknown as QuestionGenerationServiceMenu}
              items={items}
            />
          }
        />
        <ConfirmModal
          content="Are you sure? You will lose all your progress."
          isOpen={showResetModal}
          onConfirm={resetCallback}
          onClose={() => setShowResetModal(false)}
          data-testid="question-generation-reset-modal"
        />
        <ConfirmModal
          content="Are you sure you want to delete this question?"
          isOpen={!!itemToDelete}
          onConfirm={() => triggerDeleteItem()}
          onClose={() => setItemToDelete(null)}
          data-testid="question-generation-delete-modal"
        />
        <ScrollWrapper
          sx={{
            minHeight: inDialog ? 'calc(80vh)' : 'calc(100vh - 64px)',
          }}
        >
          <ShowIf If={!inDialog}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: '1rem',
                pt: '2rem',
              }}
            >
              <Typography variant="h3" component="h1">
                ChalkTalk Co-Author v1
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setIsReportErrorModalOpen(true)}
                startIcon={<i className="chalktalk-warning" />}
                sx={{
                  color: '#453535',
                  borderColor: '#453535',

                  '&:hover': {
                    borderColor: '#453535',
                    backgroundColor: 'rgba(69, 53, 53, 0.04)',
                  },
                }}
              >
                Report Issue
              </Button>
            </Box>
          </ShowIf>
          <StyledBlockWrap inDialog={inDialog}>
            <QuestionSelectorProvider>
              <QuestionGenerationMenuWrapper>
                <QuestionGenerationMenu
                  submitCallback={triggerGenerationRequest}
                  customQuestionCallback={addCustomItem}
                  resetCallback={setShowResetModal}
                  hasTask={!!taskId}
                  isLoading={isLoading}
                  state={state}
                  dispatch={dispatch}
                  isSubmitting={isSubmitting}
                  hasItems={!!items.length}
                  generating={generating}
                  setPassageMode={setPassageMode}
                  coauthorEnabled={coauthorEnabled}
                />
              </QuestionGenerationMenuWrapper>
            </QuestionSelectorProvider>
            <ShowIf If={passageMode}>
              <PassageWrapper
                style={{
                  width: '75%',
                  height: '100%',
                }}
              >
                <Passages
                  selectPassageCallback={handlePassageSelection}
                  onCloseCallback={() => setPassageMode(false)}
                />
              </PassageWrapper>
            </ShowIf>
            <ShowIf If={!passageMode}>
              <EditorWrapper>
                <QuestionGenerationEditor
                  isLoading={isLoading}
                  hasTask={!!taskId}
                  item={selectedItem}
                  isSubmitting={isSubmitting}
                  updateItem={updateItem}
                  getLoadingMessage={getLoadingMessage}
                  firstQuestionTimeRef={firstQuestionTimeRef}
                  taskId={taskId}
                  subject={state.subject}
                  questionType={String(state.questionType || 'unknown')}
                />
              </EditorWrapper>
              <ShowIf If={items.length > 0 || generationStatuses.length > 0}>
                <QuestionNavigatorWrapper>
                  <QuestionGenerationQuestions
                    generationStatuses={generationStatuses}
                    items={items}
                    selectedItem={selectedItem}
                    selectItem={selectItem}
                    deleteItem={deleteItem}
                    submitItems={submitItems}
                    isSubmitting={isSubmitting}
                  />
                </QuestionNavigatorWrapper>
              </ShowIf>
            </ShowIf>
          </StyledBlockWrap>
        </ScrollWrapper>
        <ReportErrorModal
          isOpen={isReportErrorModalOpen}
          onClose={() => setIsReportErrorModalOpen(false)}
          onSubmit={handleSubmitError}
          initialMessage={
            taskId
              ? `Issue ID: ${taskId}\n\nPlease describe the issue you encountered:`
              : ''
          }
          isErrorReport={!!taskId}
        />
      </MainContent>

      {/* Navigation confirmation dialog */}
      {showNavigationPrompt && (
        <BlockNavigationAway
          handleNavigationCancel={handleNavigationCancel}
          handleNavigationConfirm={handleNavigationConfirm}
          message="If you leave now, your question generation progress will be lost."
        />
      )}
    </>
  );
};

export default QuestionGenerationService;
